/* assets/wishlist.js — anti-flicker total (eager + priorité haute) */

const WL_KEY = 'wishlist.handles.v1';

/* ===== Utils prix/devise ===== */
function formatMoney(cents, currency = (window.Shopify?.currency?.active ?? 'EUR')) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency })
      .format((cents ?? 0) / 100);
  } catch {
    return `${(cents ?? 0) / 100} ${currency}`;
  }
}

/* ===== Wishlist (localStorage) ===== */
const Wishlist = {
  get() {
    try { return JSON.parse(localStorage.getItem(WL_KEY)) || []; }
    catch { return []; }
  },
  set(list) {
    localStorage.setItem(WL_KEY, JSON.stringify(Array.from(new Set(list))));
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { list: this.get() }}));
  },
  has(h) { return this.get().includes(h); },
  add(h){ if(!h) return; const l=this.get(); if(!l.includes(h)){ l.push(h); this.set(l);} },
  remove(h){ if(!h) return; this.set(this.get().filter(x => x!==h)); },
  toggle(h){ this.has(h) ? this.remove(h) : this.add(h); }
};

/* ===== Cart drawer Wonder ===== */
function openAndRefreshCartDrawer() {
  const el = document.querySelector('cart-drawer');
  if (!el) return;
  try { if (!el.isOpen) el.toggleDrawerClasses?.() ?? (el.open = true); } catch {}
  try { document.dispatchEvent(new CustomEvent('cart-drawer:refresh')); } catch {}
}

/* (optionnel) compteur panier */
async function getCart() {
  const r = await fetch('/cart.js', { credentials: 'same-origin' });
  if (!r.ok) throw new Error('cart fetch failed');
  try { return await r.json(); }
  catch { return { item_count: 0, items: [] }; }
}
function updateCartCountUI(cart) {
  const count = Number.isFinite(cart?.item_count) ? cart.item_count : 0;
  document.querySelectorAll(
    '.cart-count-bubble, [data-cart-count], .header__cart-count, .wt-cart__count, .cart-count'
  ).forEach(el => {
    el.textContent = count;
    el.classList.toggle('hidden', !count);
    el.setAttribute?.('aria-hidden', !count ? 'true' : 'false');
  });
}

/* ===== Accessibilité ===== */
function getAriaLive() {
  let live = document.getElementById('wl-aria-live');
  if (!live) {
    live = Object.assign(document.createElement('div'), { id: 'wl-aria-live' });
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('aria-atomic', 'true');
    live.style.position = 'absolute';
    live.style.left = '-9999px';
    document.body.appendChild(live);
  }
  return live;
}

/* ===== Cœurs sur cartes produit ===== */
function hydrateWishlistButtons(root = document) {
  root.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
    const handle = btn.getAttribute('data-product-handle');
    if (!handle) return;

    const active = Wishlist.has(handle);
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.setAttribute('aria-label', active ? 'Retirer des favoris' : 'Ajouter aux favoris');

    if (!btn._wlBound) {
      btn.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        Wishlist.toggle(handle);
        const on = Wishlist.has(handle);
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.setAttribute('aria-label', on ? 'Retirer des favoris' : 'Ajouter aux favoris');
      });
      btn._wlBound = true;
    }
  });
}

/* ===== Page Wishlist ===== */
async function fetchProductByHandle(handle) {
  const r = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
  if (!r.ok) throw new Error('404');
  return r.json();
}
function firstPurchasableVariant(product) {
  if (!product?.variants?.length) return null;
  return product.variants.find(v => v.available) || product.variants[0];
}
function productPriceCents(p) {
  if (typeof p.price === 'number') return p.price;
  const v = p?.variants?.[0];
  if (typeof v?.price === 'number') return Math.round(v.price * 100);
  if (typeof v?.price === 'string') return Math.round(parseFloat(v.price) * 100);
  return null;
}
function isAvailable(product) {
  const variant = firstPurchasableVariant(product);
  return !!variant && variant.available !== false;
}
function itemTpl(p) {
  const img = p.featured_image || (p.images && p.images[0]) || null;
  const currency = window.Shopify?.currency?.active ?? 'EUR';
  const priceCents = productPriceCents(p);
  const priceHtml = (priceCents != null) ? `<div class="wl-price">${formatMoney(priceCents, currency)}</div>` : '';
  const variant = firstPurchasableVariant(p);
  const canBuy = !!variant && variant.available !== false;
  const titleForAlt = (variant?.title && variant.title !== 'Default Title') ? `${p.title} — ${variant.title}` : p.title;

  return `
      <div class="wl-item" data-handle="${p.handle}">
        <a class="wl-thumb" href="/products/${p.handle}" aria-label="${p.title}">
          ${img ? `<img src="${img}" alt="${titleForAlt}">` : `<div class="wl-noimg" aria-hidden="true"></div>`}
          <button type="button" class="wl-remove" data-remove="${p.handle}" aria-label="Retirer">×</button>
        </a>
        <div class="wl-info">
          <a class="wl-title" href="/products/${p.handle}">${p.title}</a>
          ${priceHtml}
          <div class="wl-actions">
            <button
              type="button"
              class="wl-add"
              ${canBuy ? `data-variant-id="${variant.id}"` : 'disabled'}
              aria-label="${canBuy ? 'Ajouter au panier' : 'Rupture de stock'}">
              ${canBuy ? 'Ajouter au panier' : 'Rupture'}
            </button>
          </div>
        </div>
      </div>
    `;
}

/* Limiter la concurrence des fetch */
async function mapLimit(items, limit, iter) {
  const ret = new Array(items.length);
  let i = 0, running = 0;
  return new Promise((resolve) => {
    function next() {
      while (running < limit && i < items.length) {
        const idx = i++; running++;
        Promise.resolve(iter(items[idx]))
          .then(v => { ret[idx] = v; })
          .catch(() => { ret[idx] = null; })
          .finally(() => { running--; (i >= items.length && running === 0) ? resolve(ret) : next(); });
      }
    }
    next();
  });
}

async function renderWishlistPage() {
  const allProductsPageURL = '/collections/tous-les-produits';
  const htmlBlocks = {
    loader: `<div class="wl-grid wl-loading">Chargement…</div>`,
    emptyList: `
    <div class="wl-empty">
      <p class="wl-empty__text">Aucun favori pour le moment <span class="emoji" aria-hidden="true">💔</span></p>
      <a class="wl-cta" href="${allProductsPageURL}">Découvrir nos pièces</a>
    </div>`,
  }

  const mount = document.querySelector('[data-wishlist-page]');
  if (!mount) return;

  const handles = Wishlist.get();
  if (!handles.length) {
    mount.innerHTML = htmlBlocks.emptyList;
    return;
  }

  mount.innerHTML = htmlBlocks.loader;
  const grid = mount.querySelector('.wl-grid');

  const products = await mapLimit(handles, 8, h => fetchProductByHandle(h).catch(() => null));
  const ok = products.filter(Boolean);
  const productsAvailable = ok.filter(p => isAvailable(p));
  const productsOutOfStock = ok.filter(p => !isAvailable(p));
  const wishlistedProducts = [...productsAvailable, ...productsOutOfStock];

  if (!wishlistedProducts.length) {
    grid.innerHTML = htmlBlocks.emptyList;
    return;
  }

  grid.classList.remove('wl-loading');
  grid.innerHTML = wishlistedProducts.map(itemTpl).join('');

  /* ===== Anti-flicker image : forcer eager + priorité haute sur TOUT ===== */
  const imgs = Array.from(grid.querySelectorAll('img'));
  imgs.forEach((img) => {
    img.decoding = 'async';
    try { img.fetchPriority = 'high'; } catch {}
    try { img.loading = 'eager'; } catch {}
  });

  /* Retirer via la croix */
  grid.querySelectorAll('[data-remove]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault(); e.stopPropagation();
      const handle = btn.getAttribute('data-remove');
      Wishlist.remove(handle);
      const card = grid.querySelector(`.wl-item[data-handle="${handle}"]`);
      if (card) card.remove();
      hydrateWishlistButtons(document);
      if (!Wishlist.get().length) {
        mount.innerHTML = htmlBlocks.emptyList;
      }
    });
  });

  /* Ajouter au panier + ouvrir/refresh drawer */
  grid.querySelectorAll('.wl-add[data-variant-id]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.getAttribute('data-variant-id');
      if (!id) return;

      const original = btn.textContent;
      btn.disabled = true;
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id, quantity: 1 })
        });
        if (!res.ok) throw new Error('cart/add failed');

        try { updateCartCountUI(await getCart()); } catch(_) {}
        openAndRefreshCartDrawer();

        /* SR feedback */
        const title = btn.closest('.wl-item')?.querySelector('.wl-title')?.textContent ?? 'Produit';
        getAriaLive().textContent = `${title} ajouté au panier`;

        btn.textContent = 'Ajouté ✓';
      } catch {
        btn.textContent = 'Erreur';
      } finally {
        setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1200);
      }
    });
  });
}

/* Vider toute la wishlist si un bouton .wl-clear existe */
function clearWishlist() {
  Wishlist.set([]);
  document.dispatchEvent(new CustomEvent('wishlist:updated'));
}

/* Sync multi-onglets */
window.addEventListener('storage', (e) => {
  if (e.key === WL_KEY) document.dispatchEvent(new CustomEvent('wishlist:updated'));
});

/* ===== Init ===== */
document.addEventListener('DOMContentLoaded', () => {
  hydrateWishlistButtons(document);
  renderWishlistPage();

  const clearBtn = document.querySelector('.wl-clear');
  if (clearBtn && !clearBtn._wlBound) {
    clearBtn.addEventListener('click', clearWishlist);
    clearBtn._wlBound = true;
  }
});
document.addEventListener('shopify:section:load', (e) => hydrateWishlistButtons(e.target));
document.addEventListener('wishlist:updated', () => {
  hydrateWishlistButtons(document);
  renderWishlistPage();
});










